import { useFormContext } from 'react-hook-form';
import { useI18n } from '@react/i18n';
import { CheckoutFormValues, CheckoutState } from '../types';
import Field from './Field';
import { Collapse } from './ui';

interface Props {
    state: CheckoutState;
    /** Smartform našeptávač aktivní → přidat jeho CSS třídy na adresní pole */
    smartform?: boolean;
}

/**
 * Dodací adresa: volitelná, s výběrem uložených adres zákazníka.
 * U pickup pointů (PPL večer, ČP na poštu) jsou pole předvyplněná
 * a zamčená — checkbox je vynucený.
 */
export default function DeliveryAddressSection({ state, smartform = false }: Props) {
    const sf = (cls: string) => (smartform ? ` smartform-instance-dodaci ${cls}` : '');
    const t = useI18n();
    const { register, setValue, watch } = useFormContext<CheckoutFormValues>();
    const pickup = state.pickupPoint;
    const deliveryOpen = watch('delivery_open');

    const isReadonly = (field: string) => pickup?.readonlyFields.includes(`delivery.${field}`) ?? false;

    const applySavedAddress = (id: string) => {
        const address = state.deliveryAddresses.find((a) => String(a.id) === id);
        if (!address) {
            return;
        }
        setValue('delivery.name', address.name);
        setValue('delivery.surname', address.surname);
        setValue('delivery.company', address.company ?? '');
        setValue('delivery.street', address.street, { shouldValidate: true });
        setValue('delivery.city', address.city, { shouldValidate: true });
        setValue('delivery.zip', address.zip, { shouldValidate: true });
    };

    return (
        <fieldset>
            <div className="b-delivery clearfix">
                <label className={`b-check b-check--large${deliveryOpen ? ' active-panel' : ''}`}>
                    <span>{t('Dodací adresa')}</span>
                    {!pickup && <span>{t('(Je-li jiná než fakturační)')}</span>}
                    <input type="checkbox" disabled={!!pickup} {...register('delivery_open')} />
                </label>

                <Collapse open={deliveryOpen}>
                    <div className="b-delivery__content in">
                        {!pickup && state.deliveryAddresses.length > 0 && (
                            <div className="clearfix">
                                <div className="form-group col-xm-6 col-md-5 no-padding--left">
                                    <select
                                        className="form-control"
                                        defaultValue=""
                                        onChange={(e) => applySavedAddress(e.target.value)}
                                    >
                                        <option value="" disabled>{t('Zvolte adresu')}</option>
                                        {state.deliveryAddresses.map((address) => (
                                            <option key={address.id} value={address.id}>{address.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <Field name="delivery.company" label={t('Firma')}>
                            <input
                                id="frm-delivery-company"
                                className="form-control"
                                readOnly={isReadonly('company')}
                                {...register('delivery.company')}
                            />
                        </Field>

                        <Field name="delivery.name" label={t('Jméno')}>
                            <input
                                id="frm-delivery-name"
                                className="form-control"
                                autoComplete="given-name"
                                {...register('delivery.name')}
                            />
                        </Field>

                        <Field name="delivery.surname" label={t('Příjmení')}>
                            <input
                                id="frm-delivery-surname"
                                className="form-control"
                                autoComplete="family-name"
                                {...register('delivery.surname', {
                                    validate: (value, values) =>
                                        values.delivery.name.trim() === '' || value.trim() !== '' || t('Zadejte prosím příjmení'),
                                })}
                            />
                        </Field>

                        <Field name="delivery.street" label={t('Ulice a číslo popisné')} required>
                            <input
                                id="frm-delivery-street"
                                className={`form-control${sf('smartform-street-and-number')}`}
                                autoComplete={smartform ? 'off' : 'street-address'}
                                readOnly={isReadonly('street')}
                                {...register('delivery.street', {
                                    validate: (value, values) =>
                                        !values.delivery_open || value.trim() !== '' || t('Zadejte prosím ulici a č.p.'),
                                })}
                            />
                        </Field>

                        <Field name="delivery.city" label={t('Město')} required>
                            <input
                                id="frm-delivery-city"
                                className={`form-control${sf('smartform-city')}`}
                                autoComplete="address-level2"
                                readOnly={isReadonly('city')}
                                {...register('delivery.city', {
                                    validate: (value, values) =>
                                        !values.delivery_open || value.trim() !== '' || t('Zadejte prosím město'),
                                })}
                            />
                        </Field>

                        <div className="clearfix">
                            <div className="form-group col-xm-2 no-padding--left">
                                <label className="control-label" htmlFor="frm-delivery-zip">{t('PSČ')} <span>*</span></label>
                                <input
                                    id="frm-delivery-zip"
                                    className={`form-control${sf('smartform-zip')}`}
                                    autoComplete="postal-code"
                                    maxLength={10}
                                    readOnly={isReadonly('zip')}
                                    {...register('delivery.zip')}
                                />
                            </div>
                        </div>

                        <div className="clearfix">
                            <div className="form-group col-xm-6 col-md-5 no-padding--left">
                                <label className="control-label">{t('Stát')} <span>*</span></label>
                                <select className="form-control" disabled style={{ pointerEvents: 'none' }}>
                                    <option>{state.countryName}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
        </fieldset>
    );
}
